import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdRefresh, MdSearch, MdVisibility, MdShoppingBag, MdCurrencyRupee, MdPendingActions, MdTaskAlt, MdWarningAmber } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import { StatCard, statGridSx } from '../Reports/reportShared';
import OrderDetailsPage from './OrderDetailsPage';
import {
  FONT,
  PAYMENT_MODE,
  PAYMENT_STATUS,
  MiniProgress,
  StatusChip,
  deliveredAt,
  expectedDelivery,
  fmtDate,
  fmtDateTime,
  inr,
  isDelayed,
  itemName,
  paymentInfo,
  pill,
  timeAgo,
} from './orderShared';

const TABS = [
  { key: '', label: 'All Orders' },
  { key: 'new', label: 'New' },
  { key: 'processing', label: 'Processing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'returned', label: 'Returned' },
];
const SORTS = [
  ['date_desc', 'Newest first'],
  ['date_asc', 'Oldest first'],
  ['amount_desc', 'Amount: high to low'],
  ['amount_asc', 'Amount: low to high'],
];
const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};

const OrderList = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const params = useMemo(() => {
    const p = { sortBy };
    if (tab) p.statusGroup = tab;
    if (search) p.search = search;
    if (paymentStatus) p.paymentStatus = paymentStatus;
    if (paymentMode) p.paymentMode = paymentMode;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [tab, search, paymentStatus, paymentMode, from, to, sortBy]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await getData('order/summary', null, authToken(), false);
      if (res?.success) setSummary(res);
    } catch {
      // Cards are a nice-to-have; the table still works without them.
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData('order/all-order', { ...params, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.orders) {
        setOrders(res.orders);
        setMeta(res.meta || { total: res.orders.length });
      } else {
        setOrders([]);
        setError(res?.message || 'Could not load orders');
      }
    } catch (err) {
      setOrders([]);
      setError(err.message || 'Could not load orders');
    } finally {
      setLoading(false);
    }
  }, [params, page, rowsPerPage]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const refresh = () => {
    loadSummary();
    loadOrders();
  };
  const resetFilters = () => {
    setTab('');
    setSearchInput('');
    setSearch('');
    setPaymentStatus('');
    setPaymentMode('');
    setFrom('');
    setTo('');
    setSortBy('date_desc');
    setPage(0);
  };

  const count = (key) => (key ? summary?.groups?.find((g) => g.key === key)?.count : summary?.total) ?? 0;
  const needsAction = count('new') + count('processing');
  const deliveredPct = summary?.total ? Math.round((count('delivered') / summary.total) * 100) : 0;
  const filterCount = [search, paymentStatus, paymentMode, from, to, sortBy !== 'date_desc'].filter(Boolean).length;

  const cards = [
    { label: 'Total Orders', value: (summary?.total || 0).toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${summary?.today?.orders || 0} placed today`, onClick: () => { setTab(''); setPage(0); }, active: tab === '' },
    { label: "Today's Sales", value: inr(summary?.today?.revenue).replace('.00', ''), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `${summary?.today?.orders || 0} orders today` },
    { label: 'Needs Action', value: needsAction.toLocaleString('en-IN'), icon: MdPendingActions, gradient: summary?.stale ? 'linear-gradient(135deg,#f43f5e,#dc2626)' : 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: summary?.stale ? `${summary.stale} waiting over 2 days` : 'New + being processed', onClick: () => { setTab('new'); setPage(0); }, active: tab === 'new' },
    { label: 'Delivered', value: count('delivered').toLocaleString('en-IN'), icon: MdTaskAlt, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `${deliveredPct}% of all orders`, onClick: () => { setTab('delivered'); setPage(0); }, active: tab === 'delivered' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, background: '#f8fafc', minHeight: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT, color: '#0f172a', lineHeight: 1.2 }}>Order Management</Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', mt: 0.25 }}>Find, track and review every customer order</Typography>
        </Box>
        <Tooltip title="Refresh" arrow>
          <IconButton onClick={refresh} sx={{ background: '#fff', border: '1px solid #e2e8f0' }}>
            <MdRefresh size={20} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ ...statGridSx, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 2, mb: 2.5 }}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Status tabs with live counts */}
      <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', mb: 2.5, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t.label}
                  <Chip size="small" label={count(t.key).toLocaleString('en-IN')} sx={{ height: 20, fontSize: 11, fontWeight: 800, background: tab === t.key ? 'rgb(255 209 2)' : '#f1f5f9' }} />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', mb: 2.5 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Search order no. / customer / phone / invoice"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select size="small" fullWidth label="Payment" value={paymentStatus} onChange={(e) => { setPaymentStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(PAYMENT_STATUS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select size="small" fullWidth label="Method" value={paymentMode} onChange={(e) => { setPaymentMode(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(PAYMENT_MODE).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField size="small" fullWidth type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField size="small" fullWidth type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={8} md={3}>
            <TextField select size="small" fullWidth label="Sort by" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }}>
              {SORTS.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4} md="auto">
            <Button onClick={resetFilters} disabled={!filterCount && !tab} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>
              Clear{filterCount ? ` (${filterCount})` : ''}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Order', 'Customer', 'Items', 'Deliver To', 'Amount', 'Payment', 'Status', 'Delivery', ''].map((h, i) => (
                  <TableCell
                    key={h || i}
                    align={h === 'Amount' ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5, fontSize: 12.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', color: '#94a3b8', py: 8, fontFamily: FONT }}>
                    No orders match these filters.
                  </TableCell>
                </TableRow>
              )}
              {orders.map((o) => {
                const pay = paymentInfo(o);
                const exp = expectedDelivery(o);
                const delayed = isDelayed(o);
                const delivered = deliveredAt(o);
                const names = (o.items || []).map(itemName);
                const addr = o.shippingAddress || {};
                return (
                  <TableRow
                    key={o._id}
                    hover
                    onClick={() => navigate(`/ordermanagement/${o._id}`)}
                    sx={{ cursor: 'pointer', '&:nth-of-type(even)': { background: '#fafbfc' }, boxShadow: delayed ? 'inset 4px 0 0 #dc2626' : 'none' }}
                  >
                    <TableCell sx={{ fontFamily: FONT, minWidth: 130 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{o.orderNumber}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>{fmtDateTime(o.createdAt)}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, minWidth: 150 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: FONT }}>{o.customerId?.name || addr.name || 'Guest'}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>{addr.mobile || o.customerId?.mobile || o.customerId?.email || ''}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, maxWidth: 220 }}>
                      <Tooltip title={names.join(', ')} arrow>
                        <Box>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: FONT }}>
                            {(o.items || []).reduce((a, i) => a + (i.quantity || 0), 0)} item{(o.items || []).reduce((a, i) => a + (i.quantity || 0), 0) === 1 ? '' : 's'}
                          </Typography>
                          <Typography noWrap sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>{names[0]}{names.length > 1 ? ` +${names.length - 1} more` : ''}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontFamily: FONT }}>{addr.city || '-'}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b', fontFamily: FONT }}>{[addr.state, addr.pincode].filter(Boolean).join(' · ')}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 800, fontFamily: FONT }}>{inr(Math.abs(o.totalAmount))}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: FONT, mb: 0.5 }}>{pay.method}</Typography>
                      <Chip size="small" label={pay.label} sx={pill(pay.bg, pay.fg, { height: 22, fontSize: 11.5 })} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                      <StatusChip status={o.orderStatus} />
                      <MiniProgress status={o.orderStatus} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, whiteSpace: 'nowrap' }}>
                      {o.orderStatus === 'delivered' ? (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#166534', fontFamily: FONT }}>{delivered ? fmtDate(delivered) : 'Delivered'}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{exp ? `Expected ${fmtDate(exp)}` : ''}</Typography>
                        </>
                      ) : ['cancelled', 'returned', 'failed'].includes(o.orderStatus) ? (
                        <Typography sx={{ fontSize: 13, color: '#94a3b8', fontFamily: FONT }}>—</Typography>
                      ) : exp ? (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: delayed ? '#b91c1c' : '#0f172a', fontFamily: FONT }}>{fmtDate(exp)}</Typography>
                          {delayed ? (
                            <Chip size="small" icon={<MdWarningAmber />} label="Delayed" sx={{ ...pill('#fee2e2', '#b91c1c', { height: 20, fontSize: 11 }), '& .MuiChip-icon': { color: 'inherit', fontSize: 14 } }} />
                          ) : (
                            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{timeAgo(o.createdAt)}</Typography>
                          )}
                        </>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: '#94a3b8', fontFamily: FONT }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View order" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/ordermanagement/${o._id}`);
                          }}
                          sx={{ color: '#64748b', background: 'rgba(100,116,139,.1)', borderRadius: '8px', '&:hover': { color: '#000', background: 'rgb(255 209 2)' } }}
                        >
                          <MdVisibility size={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={meta.total || 0}
          page={Math.min(page, Math.max(Math.ceil((meta.total || 0) / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>
    </Box>
  );
};

const OrderManagement = () => {
  const { orderId } = useParams();
  return orderId ? <OrderDetailsPage orderId={orderId} /> : <OrderList />;
};

export default OrderManagement;
